import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { TourStepAction } from '../types';

interface UseStepActionsOptions {
  closeModal?: () => void;
}

/**
 * Hook to execute tour step actions (navigate, click, etc.)
 */
export function useStepActions(options: UseStepActionsOptions = {}) {
  const { closeModal } = options;
  const navigate = useNavigate();

  const executeAction = useCallback(async (action: TourStepAction): Promise<void> => {
    switch (action.type) {
      case 'navigate':
        if (action.path) {
          await navigate(action.path);
          // Wait for navigation to complete
          await new Promise(resolve => setTimeout(resolve, 300));
        }
        break;

      case 'click':
        if (action.selector) {
          // Retry loop - wait for element to exist and be enabled
          let attempts = 0;
          const maxAttempts = 10;
          while (attempts < maxAttempts) {
            const element = document.querySelector<HTMLElement>(action.selector);
            if (element) {
              const isDisabled = element.hasAttribute('disabled') ||
                element.getAttribute('aria-disabled') === 'true' ||
                (element as HTMLButtonElement).disabled;

              if (isDisabled) {
                console.log(`[ShowMe] Element disabled, waiting... (attempt ${attempts + 1})`);
                attempts++;
                await new Promise(resolve => setTimeout(resolve, 200));
                continue;
              }

              element.click();
              await new Promise(resolve => setTimeout(resolve, 300));
              break;
            } else {
              console.log(`[ShowMe] Element not found, waiting... (attempt ${attempts + 1})`);
              attempts++;
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }
          if (attempts >= maxAttempts) {
            console.warn(`[ShowMe] Element not found or disabled after ${maxAttempts} attempts: ${action.selector}`);
          }
        }
        break;

      case 'wait':
        await new Promise(resolve => setTimeout(resolve, action.delay ?? 500));
        break;

      case 'openModal':
        // Click on the element that opens the modal
        if (action.selector) {
          const element = document.querySelector<HTMLElement>(action.selector);
          if (element) {
            element.click();
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
        break;

      case 'closeModal':
        // Close any open modal
        if (closeModal) {
          closeModal();
          await new Promise(resolve => setTimeout(resolve, 300));
        } else {
          console.warn('[ShowMe] closeModal action called but no closeModal function provided');
        }
        break;

      case 'fill':
        // Fill a form field with a value (works with React Hook Form)
        if (action.selector && action.value !== undefined) {
          const input = document.querySelector<HTMLInputElement>(action.selector);
          if (input) {
            // Focus the input first
            input.focus();
            await new Promise(resolve => setTimeout(resolve, 50));

            // Use native setter to bypass React's value tracking
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
              window.HTMLInputElement.prototype,
              'value'
            )?.set;
            nativeInputValueSetter?.call(input, action.value);

            // Dispatch input event with proper InputEvent for better React compatibility
            input.dispatchEvent(new InputEvent('input', {
              bubbles: true,
              cancelable: true,
              inputType: 'insertText',
              data: action.value,
            }));

            // Also dispatch change event
            input.dispatchEvent(new Event('change', { bubbles: true }));

            // Wait a bit then blur to trigger validation and dirty state
            await new Promise(resolve => setTimeout(resolve, 100));
            input.blur();
            input.dispatchEvent(new FocusEvent('blur', { bubbles: true }));

            // Give React Hook Form time to process the change
            await new Promise(resolve => setTimeout(resolve, 200));
            console.log(`[ShowMe] Filled input: ${action.selector} with value: ${action.value}`);
          } else {
            console.warn(`[ShowMe] Input not found: ${action.selector}`);
          }
        }
        break;

      case 'fillHtml':
        // Fill a TinyMCE HTML editor with content
        if (action.value !== undefined) {
          // Wait for TinyMCE to be available (it loads asynchronously)
          let attempts = 0;
          const maxAttempts = 20;
          while (attempts < maxAttempts) {
            const tinymce = (window as unknown as { tinymce?: { activeEditor?: { setContent: (content: string) => void; fire: (event: string) => void } } }).tinymce;
            if (tinymce?.activeEditor) {
              console.log('[ShowMe] Setting TinyMCE content');
              tinymce.activeEditor.setContent(action.value);
              // Fire events to trigger React Hook Form update
              // TinyMCE React uses 'input' for onEditorChange by default
              tinymce.activeEditor.fire('input');
              tinymce.activeEditor.fire('change');
              // Wait for React state to update
              await new Promise(resolve => setTimeout(resolve, 300));
              break;
            }
            attempts++;
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          if (attempts >= maxAttempts) {
            console.warn('[ShowMe] TinyMCE editor not found after waiting');
          }
        }
        break;
    }
  }, [navigate, closeModal]);

  const executeActions = useCallback(async (actions: TourStepAction[]): Promise<void> => {
    for (const action of actions) {
      await executeAction(action);
    }
  }, [executeAction]);

  return { executeAction, executeActions };
}
